import React from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate } from 'react-router-dom';

// Contexts
import { AuthProvider } from './contexts/AuthContext';
import { BusinessProvider } from './contexts/BusinessContext';
import { useAuth } from './contexts/AuthContext';

// Layouts
import AdminLayout from './layouts/AdminLayout';

// Pages
import DashboardPage from './pages/DashboardPage';
import BusinessesPage from './pages/BusinessesPage';
import BusinessDetailPage from './pages/BusinessDetailPage';
import LoginPage from './pages/LoginPage';
import NotFoundPage from './pages/NotFoundPage';

// Protected route component
const ProtectedRoute = ({ children }) => {
  const { isAuthenticated, loading, error } = useAuth();
  
  // Show loading indicator while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }
  
  // Check authentication status from auth context
  if (!isAuthenticated) {
    // If there's an error during authentication, we can log it for debugging
    if (error) {
      console.error('Auth error in ProtectedRoute:', error);
    }
    return <Navigate to="/login" replace />;
  }
  
  return children;
};

// Admin-only route component
const AdminOnlyRoute = ({ children }) => {
  const { isPlatformAdmin, isAuthenticated, loading, error } = useAuth();
  
  // Show loading indicator while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // If there's an error during authentication, we can log it for debugging
    if (error) {
      console.error('Auth error in AdminOnlyRoute:', error);
    }
    return <Navigate to="/login" replace />;
  }
  
  if (!isPlatformAdmin) {
    return <Navigate to="/businesses" replace />;
  }
  
  return children;
};

// Home route - redirects based on user role
const HomeRoute = () => {
  const { isPlatformAdmin, isAuthenticated, loading, error } = useAuth();
  
  // Show loading indicator while auth state is being determined
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-primary-500"></div>
        <span className="sr-only">Loading...</span>
      </div>
    );
  }
  
  if (!isAuthenticated) {
    // If there's an error during authentication, we can log it for debugging
    if (error) {
      console.error('Auth error in HomeRoute:', error);
    }
    return <Navigate to="/login" replace />;
  }
  
  // For platform admins, go to the dashboard
  if (isPlatformAdmin) {
    console.log('Platform admin detected, redirecting to dashboard');
    return <Navigate to="/dashboard" replace />;
  } else {
    // For regular users, go to the businesses page
    console.log('Regular user detected, redirecting to businesses');
    return <Navigate to="/businesses" replace />;
  }
};

// Main App component with global loading indicator
function App() {
  return (
    <Router>
      <AuthProvider>
        <AppContent />
      </AuthProvider>
    </Router>
  );
}

// Separate component to use auth context
function AppContent() {
  const { loading: authLoading } = useAuth();
  
  // Show a global loading spinner during initial auth check
  if (authLoading) {
    return (
      <div className="min-h-screen flex flex-col items-center justify-center bg-dark-900">
        <div className="animate-spin rounded-full h-16 w-16 border-t-2 border-b-2 border-primary-500 mb-4"></div>
        <p className="text-gray-400">Loading application...</p>
      </div>
    );
  }
  
  return (
    <BusinessProvider>
      <Routes>
        {/* Public routes */}
        <Route path="/login" element={<LoginPage />} />
        
        {/* Home route - redirects based on user role */}
        <Route path="/" element={<HomeRoute />} />
        
        {/* Admin-only routes */}
        <Route 
          path="/dashboard" 
          element={
            <AdminOnlyRoute>
              <AdminLayout><DashboardPage /></AdminLayout>
            </AdminOnlyRoute>
          } 
        />
        
        {/* Protected routes with AdminLayout */}
        <Route 
          path="/businesses" 
          element={
            <ProtectedRoute>
              <AdminLayout><BusinessesPage /></AdminLayout>
            </ProtectedRoute>
          } 
        />
        <Route 
          path="/businesses/create" 
          element={
            <AdminOnlyRoute>
              <AdminLayout><BusinessesPage createMode={true} /></AdminLayout>
            </AdminOnlyRoute>
          } 
        />
        <Route 
          path="/businesses/:id" 
          element={
            <ProtectedRoute>
              <AdminLayout><BusinessDetailPage /></AdminLayout>
            </ProtectedRoute>
          } 
        />
        {/* Removed standalone menu, users, and settings routes as they are now tabs in the business detail page */}
        
        {/* 404 route */}
        <Route path="*" element={<AdminLayout><NotFoundPage /></AdminLayout>} />
      </Routes>
    </BusinessProvider>
  );
}

export default App;